import React, { useState, useMemo, useRef } from 'react';
import { Project } from '../../types';
import Icon from '../../utils/icons';
import './Projects.css';

const FeaturedStrip = React.memo<{ projects: Project[]; categoryColors: Record<string, { bg: string; text: string }> }>(({ projects, categoryColors }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const featured = projects.filter(p => p.featured);

    const scroll = (dir: number) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
        }
    };

    return (
        <div className="featured-strip-wrapper">
            <div className="featured-strip-header">
                <span className="featured-strip-label"><Icon name="star" size={16} /> Featured Work</span>
                <div className="strip-nav">
                    <button className="strip-nav-btn" onClick={() => scroll(-1)} aria-label="Scroll left">‹</button>
                    <button className="strip-nav-btn" onClick={() => scroll(1)}  aria-label="Scroll right">›</button>
                </div>
            </div>
            <div className="featured-strip" ref={scrollRef}>
                {featured.map((project, i) => {
                    const catColor = categoryColors[project.category] || categoryColors['Web'];
                    return (
                        <div key={i} className="strip-card" style={{ '--strip-color': catColor.bg } as React.CSSProperties}>
                            <div className="strip-card-stripe" style={{ background: catColor.bg }} />
                            <div className="strip-card-body">
                                <div className="strip-card-badges">
                                    <span className="strip-badge" style={{ background: catColor.bg, color: catColor.text }}>
                                        {project.category}
                                    </span>
                                    <span className="strip-status"><Icon name="check" size={12} /> Live</span>
                                </div>
                                <h4 className="strip-card-title">{project.title}</h4>
                                <p className="strip-card-desc">{project.description}</p>
                                <div className="strip-card-footer">
                                    <div className="strip-techs">
                                        {project.technologies.slice(0, 3).map((t, j) => (
                                            <span key={j} className="strip-tech">{t}</span>
                                        ))}
                                        {project.technologies.length > 3 && (
                                            <span className="strip-tech muted">+{project.technologies.length - 3}</span>
                                        )}
                                    </div>
                                    <div className="strip-links">
                                        {project.liveLink && (
                                            <a href={project.liveLink} target="_blank" rel="noopener noreferrer"
                                               className="strip-link-btn" style={{ background: catColor.bg }}>
                                                {project.liveLabel || 'Live'} ↗
                                            </a>
                                        )}
                                        {project.liveLink2 && (
                                            <a href={project.liveLink2} target="_blank" rel="noopener noreferrer"
                                               className="strip-link-btn alt">
                                                {project.liveLabel2} ↗
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

const categoryColors: Record<string, { bg: string; text: string }> = {
    'All':    { bg: 'var(--primary-color)', text: '#fff' },
    'Web3':   { bg: '#7c3aed', text: '#fff' },
    'Web':    { bg: '#0369a1', text: '#fff' },
    'Mobile': { bg: '#059669', text: '#fff' },
    'UN':     { bg: '#0f6cbf', text: '#fff' },
};

const statusConfig: Record<string, { icon: React.ReactNode; label: string }> = {
    'Live':      { icon: <Icon name="check" size={14} />, label: 'Live' },
    'Completed': { icon: <Icon name="check" size={14} />, label: 'Completed' },
    'In Progress': { icon: <Icon name="refresh" size={14} />, label: 'In Progress' },
};

interface Props {
    projects: Project[];
}

const Projects: React.FC<Props> = ({ projects }) => {
    const [activeFilter, setActiveFilter] = useState('All');

    const categories = useMemo(() => {
        return ['All', ...Array.from(new Set(projects.map(p => p.category)))];
    }, [projects]);

    const filtered = useMemo(() => {
        if (activeFilter === 'All') return projects;
        return projects.filter(p => p.category === activeFilter);
    }, [projects, activeFilter]);

    return (
        <div className="projects-wrapper">
            <FeaturedStrip projects={projects} categoryColors={categoryColors} />

            <div className="projects-filter-bar">
                {categories.map(cat => (
                    <button
                        key={cat}
                        className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
                        style={activeFilter === cat ? {
                            background: categoryColors[cat]?.bg || 'var(--primary-color)',
                            color: categoryColors[cat]?.text || '#fff',
                        } as React.CSSProperties : {}}
                        onClick={() => setActiveFilter(cat)}
                        aria-pressed={activeFilter === cat}
                        aria-label={`Filter by ${cat} — ${cat === 'All' ? projects.length : projects.filter(p => p.category === cat).length} project${projects.length === 1 ? '' : 's'}`}
                    >
                        {cat}
                        <span className="filter-count" aria-hidden="true">
                            {cat === 'All' ? projects.length : projects.filter(p => p.category === cat).length}
                        </span>
                    </button>
                ))}
            </div>

            <div
                className="projects-grid"
                role="region"
                aria-label={`${activeFilter} projects`}
                aria-live="polite"
                aria-atomic="false"
            >
                {filtered.length === 0 ? (
                    <div className="projects-empty" role="status">
                        <span aria-hidden="true"><Icon name="search" size={20} /></span>
                        <p>No projects in this category yet — check back soon!</p>
                        <button className="filter-btn" onClick={() => setActiveFilter('All')}>
                            Show all projects
                        </button>
                    </div>
                ) : filtered.map((project, index) => {
                    const catColor = categoryColors[project.category] || categoryColors['Web'];
                    const status = statusConfig[project.status] || statusConfig['Completed'];
                    return (
                        <div
                            key={index}
                            className={`project-card ${project.featured ? 'featured' : ''}`}
                            style={{ '--cat-color': catColor.bg } as React.CSSProperties}
                        >
                            <div className="card-stripe" style={{ background: catColor.bg }} />

                            <div className="card-badges">
                                <span
                                    className="badge category-badge"
                                    style={{ background: catColor.bg, color: catColor.text }}
                                >
                                    {project.category}
                                </span>
                                {project.featured && (
                                    <span className="badge featured-badge"><Icon name="star" size={12} /> Featured</span>
                                )}
                                <span className="badge status-badge">
                                    {status.icon} {status.label}
                                </span>
                            </div>

                            <h3 className="project-title">{project.title}</h3>

                            <p className="project-description">{project.description}</p>

                            {project.highlights && project.highlights.length > 0 && (
                                <ul className="project-highlights">
                                    {project.highlights.map((h, i) => (
                                        <li key={i}>{h}</li>
                                    ))}
                                </ul>
                            )}

                            <div className="card-footer">
                                <div className="project-technologies">
                                    {project.technologies.map((tech, i) => (
                                        <span key={i} className="tech-tag">{tech}</span>
                                    ))}
                                </div>

                                {(project.githubLink || project.liveLink || project.liveLink2) && (
                                    <div className="project-links">
                                        {project.githubLink && (
                                            <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="link-btn github-btn">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                                                </svg>
                                                GitHub
                                            </a>
                                        )}
                                        {project.liveLink && (
                                            <a href={project.liveLink} target="_blank" rel="noopener noreferrer" className="link-btn live-btn" style={{ background: catColor.bg }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                                                    <polyline points="15 3 21 3 21 9"/>
                                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                                </svg>
                                                {project.liveLabel || 'Live'}
                                            </a>
                                        )}
                                        {project.liveLink2 && (
                                            <a href={project.liveLink2} target="_blank" rel="noopener noreferrer" className="link-btn live-btn live-btn-alt">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                                                    <polyline points="15 3 21 3 21 9"/>
                                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                                </svg>
                                                {project.liveLabel2 || 'Live 2'}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(Projects);
